import { AlertTriangle, CreditCard, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";

export function PaymentWarningBanner() {
  const { subscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);

  // Only show for past_due subscriptions with active grace period
  if (!subscription || subscription.status !== 'past_due' || !subscription.grace_period_ends_at) {
    return null;
  }

  const gracePeriodEnd = new Date(subscription.grace_period_ends_at);
  const now = new Date();
  
  // Don't show if grace period has already expired
  if (gracePeriodEnd <= now) {
    return null;
  }

  const hoursRemaining = Math.max(0, (gracePeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60));
  const daysRemaining = Math.ceil(hoursRemaining / 24);
  const isUrgent = daysRemaining <= 1;

  const handleUpdatePayment = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open customer portal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Alert 
      variant="destructive" 
      className={`mb-6 ${isUrgent ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'}`}
    >
      <AlertTriangle className={`h-5 w-5 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />
      <AlertTitle className={`text-lg font-semibold ${isUrgent ? 'text-red-800 dark:text-red-200' : 'text-orange-800 dark:text-orange-200'}`}>
        {isUrgent ? '🚨 URGENT: Payment Required Today' : '⚠️ Payment Issue Detected'}
      </AlertTitle>
      <AlertDescription className={`mt-2 ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-orange-700 dark:text-orange-300'}`}>
        <div className="space-y-3">
          <p>
            Your last payment could not be processed. Please update your payment method 
            by <strong>{format(gracePeriodEnd, 'MMMM d, yyyy')}</strong> ({formatDistanceToNow(gracePeriodEnd, { addSuffix: true })}) 
            to avoid losing your <strong>{subscription.credits_remaining}</strong> remaining credits.
          </p>
          
          <div className="flex items-center gap-4 flex-wrap">
            <Button 
              onClick={handleUpdatePayment}
              disabled={isLoading}
              className={isUrgent ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {isLoading ? 'Opening...' : 'Update Payment Method'}
            </Button>
            
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" />
              <span>
                {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
              </span>
            </div>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
